import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { ArrowRight } from "lucide-react";

const HERO_IMG = "/login-cover.jpg";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-brand-page relative z-10">
      {/* Left: image */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_IMG} alt="Bond Collective workspace" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-brand-ink/40" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-brand-page">
          <div className="font-serif text-2xl tracking-tight">Bond Collective</div>
          <div>
            <p className="font-serif text-4xl xl:text-5xl font-light leading-tight max-w-md">
              A clear view of every location's performance.
            </p>
            <p className="mt-4 text-sm text-brand-page/70 max-w-sm">
              The private data hub for Bond Collective's portfolio of workspaces.
            </p>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="lg:hidden font-serif text-2xl mb-10 text-brand-ink">Bond Collective</div>
          <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Bond Collective Data Hub</p>
          <h1 className="font-serif text-4xl font-light tracking-tight mt-3 text-brand-ink">Sign in</h1>
          <p className="text-brand-muted text-sm mt-2 mb-10">Access your financial performance dashboard.</p>

          <form onSubmit={submit} className="space-y-8" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2.5 text-brand-ink focus:outline-none focus:border-brand-primary transition-colors duration-200"
                placeholder="you@bondcollective.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full bg-transparent border-0 border-b border-brand-borderStrong py-2.5 text-brand-ink focus:outline-none focus:border-brand-primary transition-colors duration-200"
                placeholder={"\u2022".repeat(8)}
              />
            </div>

            {error && (
              <p className="text-sm text-brand-neg" data-testid="login-error">{error}</p>
            )}

            <button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="group w-full flex items-center justify-center gap-2 bg-brand-primary text-brand-page py-3.5 text-sm tracking-wide hover:bg-brand-primaryHover active:scale-[0.98] transition-all duration-200 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform duration-200" />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
